import {createSelector} from '@reduxjs/toolkit'

import {eq, minZero} from '@shared/helpers/lang'

import {
    APIStatus,
    ChargePointPolicy,
    MaximumUsingChargePointAmount,
    MinimumChargePointAmount,
    PAYMONEY_RESULT_TYPE,
    PayMoneyUserAvailablePayStates,
    PayMoneyUserStatus,
    PayPointAmount,
    RechargePaymentStatus,
    RechargePaymentStatusType,
    RechargePointLimitType,
} from '@web/core/constants'
import {isPointChargingAvailable, getRechargePointAmount} from '@web/helpers/policies'
import {accountSelector, bankEasyPayAccountInfoSelector, rechargeAccountInfoSelector} from '@web/slices/account/slice'
import {AccumulateRateBenefitCase, chargePointSelector, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {payLaterSelector} from '@web/slices/payLater/slice'
import {paymentSelector, payMethodAvailableSelector, payMethodInfoSelector} from '@web/slices/paymentMethod/slice'
import {purchaseRewardSelector} from '@web/slices/reward/slice'

import {PaymoneyDisableCase} from './constants'
import {paymentAmountInfoSelectors} from './paymentAmount'

/** 충전결제 정보 타입 */
export type RechargePaymentInfo = {
    /**
     * 충전결제 상태. 현재는 결제수단 선택불가 사유 포함.
     */
    status: RechargePaymentStatusType
    /**
     * 충전결제 충전 한도
     * - 최소 충전 미만 금액은 절삭
     */
    reloadableRechargePointAmount: number
    /** 충전결제 선택중 페이머니 사용 포인트 여부 */
    shouldChargePointUsed: boolean
    /** 충전결제 진행중 충전 진행 여부 */
    shouldRecharging: boolean
    /** 충전결제 선택 여부 */
    rechargePointEnabled: boolean
    /** 충전금 수정이 필요한지 여부 */
    isNeedRecharging: boolean
    /** 충전결제 사용 가능 여부 */
    shouldRecharge: boolean
    /** 충전결제 유도 가능 여부 */
    shouldPrompt: boolean
    /** 충전결제 유도 배너 노출가능 여부 */
    shouldPromptBanner: boolean
    /** 충전 불가 사유 */
    ineligibilityReason: {
        /** 후불결제 사용 시 충전결제 이용 불가 */
        isDisabledByPayLater: boolean
        /**
         * 최소 충전 가능 여부
         * - 충전결제 잔여한도가 최소 사용금액보다 적은 경우
         * - 충전결제가 부족하면 선택되지 않습니다.
         * - 페이머니 충전결제 / 페이머니 로 결제가 가능한 경우 (1.0, 3.0, 4.0) 최소 충전 가능여부를 확인 합니다.
         */
        isRechargingPointLack: boolean
        /** 월 충전한도를 초과 */
        isOneMonthRechargeLimited: boolean
    }
    /** 복합결제 상황 */
    complex: {
        /**
         * 복합결제 가능여부
         * - 충전계좌가 등록되어 있지 않다면, 복합결제 사용 불가
         * - 모든 충전계좌가 점검중이라면, 복합결제 사용불가
         * - 선택된 충전계좌가 점검중이라면, 복합결제 사용불가
         * - 충전한도 조회 성공 X && 1.0 / 3.0 유저라면, 복합결제 사용불가
         */
        available: boolean
        /**
         * 충전결제 사용시 복합결제 필요여부 (아래의 조건을 모두 만족)
         * - 결제금액이 0보다 커야 함
         * - 충전가능한도보다 결제금액이 큰 경우
         * - 4.0 충전이 불가능한 상태일때 보유 페이머니로 결제가 불가능할 경우
         */
        isRequired: boolean
    }
}

/** 충전결제 info selector */
export const rechargePaymentInfoSelector = createSelector(
    paymentAmountInfoSelectors,
    payMoneyInfoSelector,
    chargePointSelector,
    payLaterSelector,
    rechargeAccountInfoSelector,
    payMethodAvailableSelector,
    payMethodInfoSelector,
    paymentSelector,
    (
        {paymentAmount, rechargePaymentAmount},
        {
            payMoneyUserStatus,
            isPayMoneyUser,
            availablePayment,
            isChargeLimitRequestSucceed,
            isChargeLimitRequestRetryAvailable,
            isPayMoneyRequestSucceed,
            isPayMoneyOnlyRequired,
            isUsedLikePayMoney,
            chargePointPolicy,
        },
        {
            amount: chargePointAmount,
            status: chargePointStatus,
            recharging: {amount: rechargingAmount},
            reloadable: {amount: reloadableRechargePoint, limitedType},
        },
        {enabled: payLaterUsed},
        {isRegistered: hasRechargedAccounts, selected: {payAvailable: selectedAccountPayAvailable}, maintenance},
        {isRechargePointAvailable},
        {selectedRechargePayMethod},
        {complex: {availableService}},
    ): RechargePaymentInfo => {
        const isChargePointStatus =
            chargePointStatus === APIStatus.RESOLVED && isPayMoneyRequestSucceed && !isChargeLimitRequestRetryAvailable
        const availableChargePointMethod = PayMoneyUserAvailablePayStates.includes(
            payMoneyUserStatus ?? PayMoneyUserStatus.NOT_OPEN,
        )
        const availableComplexPayment = (function () {
            const canUseRechargeAccount = selectedAccountPayAvailable && isChargeLimitRequestSucceed
            // 페이머니로 200만원까지 소진 가능 여부
            const isOverMaxChargePoint = isPayMoneyUser && (chargePointAmount ?? 0) >= MaximumUsingChargePointAmount
            const isAllBankUnderMaintenance = !hasRechargedAccounts && !!maintenance

            // 결제가 가능한 상태가 아니라면, 복합결제가 불가능하다.
            // 충전계좌가 없다면 복합결제가 불가능하다 단, 페이머니 제휴통장 미가입자 AND 충전계좌 미등록 AND 포인트가 있을경우 가능
            const availableRechargePayMethod =
                availableChargePointMethod &&
                ((!isPayMoneyUser && isUsedLikePayMoney && chargePointAmount > 0) ||
                    hasRechargedAccounts ||
                    isOverMaxChargePoint ||
                    isAllBankUnderMaintenance) &&
                isChargePointStatus
            // 복합결제 사용가능 여부
            const canUseComplexPayment = canUseRechargeAccount && availableRechargePayMethod
            if (isUsedLikePayMoney) {
                const hasChargePoint = (chargePointAmount ?? 0) > 0
                return (
                    // 보유페이머니가 없고 점검중이면 복합결제가 불가능하다
                    (chargePointAmount === 0 && canUseComplexPayment) ||
                    // 충전결제가 점검중이더라도 페이머니만으로 복합결제가 가능하다
                    (hasChargePoint && (availableRechargePayMethod || isPayMoneyOnlyRequired))
                )
            }
            return canUseComplexPayment
        })()

        /** 최소 충전 미만 금액은 절삭 */
        const reloadableRechargePointAmount =
            reloadableRechargePoint >= MinimumChargePointAmount ? reloadableRechargePoint : 0

        const isComplexPaymentRequired = (function () {
            // 페이머니 미가입 유저는 포인트 적용후 충전 가능 금액으로 확인한다.
            let usableChargePoint = !!maintenance || !selectedAccountPayAvailable ? 0 : reloadableRechargePointAmount
            if (isUsedLikePayMoney) {
                // 페이머니 가입 유저는 페이머니 적용후 충전 가능 금액으로 확인한다.
                usableChargePoint = Math.min(
                    usableChargePoint + Math.min(chargePointAmount, MaximumUsingChargePointAmount),
                    MaximumUsingChargePointAmount,
                )
            }

            return paymentAmount > 0 && paymentAmount > usableChargePoint && availableComplexPayment
        })()

        /** 후불결제 사용 시 충전결제 이용 불가 */
        const isDisabledByPayLater = !isPayMoneyUser && payLaterUsed

        /**
         * 충전가능 금액 부족으로 인해 충전결제 선택 불가 여부
         * - 보유머니는 사용 가능
         * - 보유머니 없고 && 충전결제 잔여한도가 최소 충전금액보다 적은 경우는 충전결제 선택 불가
         */
        const isReloadableRechargePointLack =
            ((!isPayMoneyUser && eq(chargePointPolicy, ChargePointPolicy.DEFAULT)) ||
                (isUsedLikePayMoney && chargePointAmount === 0)) &&
            reloadableRechargePointAmount < PayPointAmount.MINIMUM_RECHARGE_AMOUNT

        /** 최소 충전 가능 여부 */
        const isRechargingPointLack = isReloadableRechargePointLack && isChargeLimitRequestSucceed && availablePayment

        const eqLimitedType = eq(limitedType)
        /** 월 충전한도를 초과 */
        const isOneMonthRechargeLimited =
            (eqLimitedType(RechargePointLimitType.MONTH) || eqLimitedType(RechargePointLimitType.MINOR)) &&
            isReloadableRechargePointLack

        /** 충전결제 사용 가능 여부 */
        const shouldRechargePrompt =
            isChargePointStatus &&
            isRechargePointAvailable &&
            !isDisabledByPayLater &&
            !isRechargingPointLack &&
            !isOneMonthRechargeLimited &&
            paymentAmount > 0

        /** 충전결제 사용 가능 여부 */
        const shouldRecharge = shouldRechargePrompt && availableChargePointMethod

        const canBeRecharging = !selectedRechargePayMethod && !isComplexPaymentRequired && selectedAccountPayAvailable
        const shouldPrompt = shouldRecharge && canBeRecharging
        const shouldPromptBanner = shouldRechargePrompt && canBeRecharging
        const rechargePointEnabled = paymentAmount > 0 && selectedRechargePayMethod

        return {
            /**
             * 충전결제 상태
             * @TODO : payMoneyDisabledSelector 와 통합필요. 현재는 결제수단 선택불가 사유로 사용되고있음
             */
            status: (function (): RechargePaymentStatusType {
                if (paymentAmount <= 0) {
                    return RechargePaymentStatus.PAY_METHOD_NO_PAYMENT
                }
                if (isDisabledByPayLater) {
                    return RechargePaymentStatus.RECHARGE_POINT_DISABLED_BY_PAY_LATER
                }
                if (!isChargePointStatus) {
                    return RechargePaymentStatus.CHARGE_POINT_FAIL
                }
                if (!availableChargePointMethod) {
                    return RechargePaymentStatus.NOT_RECHARGE_USER
                }
                if (isOneMonthRechargeLimited) {
                    return RechargePaymentStatus.ONE_MONTH_RECHARGE_LIMITED
                }
                if (isRechargingPointLack) {
                    return RechargePaymentStatus.RECHARGE_POINT_LACK
                }
                /**
                 * TODO:(표준결제창 - 실가맹점연동) 머니 사용시 복합결제가 필요한 케이스면 결제 불가 모달 노출 필요
                 * 오픈스펙으로는 고려하지 않는 케이스여서 모달 디자인이 안되어있어서 그나마 적합한 모달인 충전가능금액 부족 모달로 임시 매칭
                 */
                if (isComplexPaymentRequired) {
                    /** 복합결제가 필요한데 복합결제가 불가능한 서비스인 경우 */
                    if (!availableService) {
                        return RechargePaymentStatus.COMPLEX_PAYMENT_UNAVAILABLE
                    }
                    if (availablePayment && isChargeLimitRequestSucceed) {
                        if (
                            (eqLimitedType(RechargePointLimitType.MONTH) ||
                                eqLimitedType(RechargePointLimitType.MINOR)) &&
                            isPayMoneyUser &&
                            chargePointAmount < MaximumUsingChargePointAmount &&
                            reloadableRechargePointAmount < PayPointAmount.MINIMUM_RECHARGE_AMOUNT
                        ) {
                            return RechargePaymentStatus.ONE_MONTH_RECHARGE_LIMITED_AND_USE_COMPLEX
                        }
                        // 보유 페이머니로 결제가 불가능할때, 점검중이라면, 남은 결제수단 DIALOG 노출
                        const shouldSelectAdditionalPayMethodDialogShow =
                            chargePointAmount < MaximumUsingChargePointAmount && !selectedAccountPayAvailable
                        // 포인트를 비활성 -> 활성처리 할 때 복합결제가 필요한 경우, 다이얼로그를 노출합니다.
                        return shouldSelectAdditionalPayMethodDialogShow
                            ? RechargePaymentStatus.SELECT_ADDITIONAL_PAY_METHOD
                            : RechargePaymentStatus.PAYMENT_USE_COMPLEX
                    }
                }
                return RechargePaymentStatus.NORMAL
            })(),
            /**
             * 충전결제 충전 한도
             * - 최소 충전 미만 금액은 절삭
             **/
            reloadableRechargePointAmount,
            /** 충전결제 선택중 페이머니 사용 포인트 여부 */
            shouldChargePointUsed: (isPayMoneyUser ? chargePointAmount : 0) > 0,
            /** 충전결제 선택중 충전 여부 */
            shouldRecharging: rechargePointEnabled && rechargingAmount > 0 && rechargePaymentAmount > 0,
            /** 충전결제 선택중 여부 */
            rechargePointEnabled,
            /** 충전금 수정이 필요한지 여부 */
            isNeedRecharging:
                selectedRechargePayMethod &&
                ((rechargingAmount === 0 && shouldRecharge) ||
                    (rechargingAmount > 0 && !(shouldRecharge && selectedAccountPayAvailable))),
            /**
             * 충전결제 사용 가능 여부
             */
            shouldRecharge,
            /** 충전결제 유도 가능 여부 */
            shouldPrompt,
            /** 충전결제 유도 배너 노출가능 여부 */
            shouldPromptBanner,
            /** 충전 불가 사유 */
            ineligibilityReason: {
                /** 후불결제 사용 시 충전결제 이용 불가 */
                isDisabledByPayLater,
                /**
                 * 최소 충전 가능 여부
                 * - 충전결제 잔여한도가 최소 사용금액보다 적은 경우
                 * - 충전결제가 부족하면 선택되지 않습니다.
                 * - 페이머니 충전결제 / 페이머니 로 결제가 가능한 경우 (1.0, 3.0, 4.0) 최소 충전 가능여부를 확인 합니다.
                 */
                isRechargingPointLack,
                /** 월 충전한도를 초과 */
                isOneMonthRechargeLimited,
            },
            /** 복합결제 상황 */
            complex: {
                /**
                 * 복합결제 가능여부
                 * - 충전계좌가 등록되어 있지 않다면, 복합결제 사용 불가
                 * - 모든 충전계좌가 점검중이라면, 복합결제 사용불가
                 * - 선택된 충전계좌가 점검중이라면, 복합결제 사용불가
                 * - 충전한도 조회 성공 X && 1.0 / 3.0 유저라면, 복합결제 사용불가
                 **/
                available: shouldRecharge && availableComplexPayment,
                /**
                 * 충전결제 사용시 복합결제 필요여부 (아래의 조건을 모두 만족)
                 * - 결제금액이 0보다 커야 함
                 * - 충전가능한도보다 결제금액이 큰 경우
                 * - 4.0 충전이 불가능한 상태일때 보유 페이머니로 결제가 불가능할 경우
                 */
                isRequired: isComplexPaymentRequired,
            },
        }
    },
)

/**
 * 충전결제로 전환시 받을 수 있는 최대 예상 적립금
 * - 선택한 충전계좌를 기준으로 네이버 통장 예상 적립금 적용
 * - 선택한 계좌를 기준으로 네이버 통장 예상 적립금 적용
 * - 카드 간편결제의 경우 고정적 3% 예상 적립금 적용
 * - 페이머니 가입유저는 충전 결제 예상만 적용
 * - 디컨 최대 예상 적립금은 3% 고정
 */
export const expectedMaxPurchaseRewardInfoWithRechargeSelector = createSelector(
    purchaseRewardSelector,
    rechargeAccountInfoSelector,
    bankEasyPayAccountInfoSelector,
    payMoneyInfoSelector,
    (
        {total: {purchase, expectedChargePoint, expectedNaverBankbook}},
        {selected: {isNaverAccount: isNaverAccountWithRecharge}, isRegistered},
        {selected: {isNaverAccount: isNaverAccountWithEasyBank}},
        {isPayMoneyUser},
    ) => {
        // 충전결제로 전환시 예상적립금. 소수점은 버림하고 계산
        const expectRewardPointChargePoint = Math.floor(purchase.amount + expectedChargePoint)
        // 네이버 통장으로 충전결제 전환시 예상 적림금. 소수점은 버림하고 계산
        const expectRewardPointNaverBankbook = Math.floor(expectRewardPointChargePoint + expectedNaverBankbook)

        // 선택한 충전계좌가 네이버 통장이라면, 네이버 통장 충전예상 예상
        // 충전계좌가 없다면 네이버 통장도 포함한 충전예상
        const rewardAmountWithSelectedChargeAccount = (function () {
            if (isPayMoneyUser || (!isPayMoneyUser && (isNaverAccountWithRecharge || !isRegistered))) {
                return expectRewardPointNaverBankbook
            }
            return expectRewardPointChargePoint
        })()

        // 선택한 계좌를 충전계좌로 선택시 네이버 통장이라면, 네이버 통장 충전예상 적용
        const rewardAmountWithSelectedBankAccount =
            isPayMoneyUser || (!isPayMoneyUser && isNaverAccountWithEasyBank)
                ? expectRewardPointNaverBankbook
                : expectRewardPointChargePoint

        // 최대 3% 적용
        const maxPurchaseRewardAmount = expectRewardPointNaverBankbook

        return {
            /** 선택한 충전계좌로 충전할 시 (일반결제) */
            rewardAmountWithSelectedChargeAccount,
            /** 선택한 계좌로 충전결제 할시 (계좌간편결제) */
            rewardAmountWithSelectedBankAccount,
            /**
             * - 카드간편결제에서 충전결제로 전환할시 최대 3% 적용 (카드간편결제, 페이머니)
             * - 디컨에서는 항상 3% 적용
             */
            maxPurchaseRewardAmount,
        }
    },
)

/** 충전결제 사용시 적립률 info Selector */
/** @TODO : 결제수단 별 충전결제 사용시 '고정' 적립률 필드 추가 */
export const rechargePointAccumulateInfoSelector = createSelector(
    rechargeAccountInfoSelector,
    payMoneyInfoSelector,
    chargePointSelector,
    (
        {isRegistered, selected: {isNaverAccount}, maintenance},
        {isPayMoneyUser},
        {recharging: {accumulateStatus, accumulateRate, expectedAccumulateRate}},
    ) => {
        const isMaxType = accumulateStatus === 'MAX'
        return {
            accumulateType: (() => {
                if (isMaxType || ((!isRegistered || maintenance) && !isPayMoneyUser)) {
                    return 'MAX'
                }
                return 'NORMAL'
            })(),
            /**
             * 충전결제 적립 타입별 적립률
             * - 페이머니에 가입한 유저일 경우 3
             * - 충전결제 계좌에 네이버 통장이 포함된 경우 3
             * - 충전결제 계좌에 네이버 통장이 포함되지 않은 경우 2.5
             * @check - 적립률 조회 API를 통해 값을 받아온 경우 expectedAccumulateRate 유효,
             *        - 받아오지 않은경우 조건들을 통해 직접 계산한 값 사용
             *        - 추후 적립률 조회 API를 모두 연동한 경우 expectedAccumulateRate값만 사용
             */
            accumulateRate:
                expectedAccumulateRate ??
                (() => {
                    if (isMaxType || ((!isRegistered || maintenance) && !isPayMoneyUser)) {
                        return accumulateRate[AccumulateRateBenefitCase.MAX]
                    }
                    if (isNaverAccount) {
                        return accumulateRate[AccumulateRateBenefitCase.NAVER_PARTNER_BANK_BOOK]
                    }
                    if (isPayMoneyUser) {
                        return accumulateRate[AccumulateRateBenefitCase.PAYMONEY_USER]
                    }
                    return accumulateRate[AccumulateRateBenefitCase.DEFAULT]
                })(),
        }
    },
)

/** 충전포인트를 반환합니다 */
export const rechargeablePointAmountSelector = createSelector(
    paymentAmountInfoSelectors,
    chargePointSelector,
    rechargeAccountInfoSelector,
    payLaterSelector,
    payMoneyInfoSelector,
    rechargePaymentInfoSelector,
    payMethodAvailableSelector,
    (
        {paymentAmount},
        {amount: chargePointAmount},
        {selected: {payAvailable: canUseRechargeAccount}},
        {enabled: payLaterUsed},
        {payMoneyUserStatus, chargePointPolicy, isUsedLikePayMoney},
        {reloadableRechargePointAmount},
        {isRechargePointAvailable},
    ) => {
        /** 충전포인트 페이머니 취급여부 */
        const isPayMoneyPolicy = eq(chargePointPolicy, ChargePointPolicy.PAYMONEY)
        const canUseChargePointAccount = isPointChargingAvailable({payMoneyUserStatus}) || isPayMoneyPolicy
        return getRechargePointAmount({
            orderAmount: paymentAmount,
            usingChargePointAmount: canUseChargePointAccount
                ? minZero(Math.min(paymentAmount, Math.min(chargePointAmount, MaximumUsingChargePointAmount)))
                : 0,
            rechargePoint: {
                enabled: true,
                usePointAll: true,
                rechargingAmount: 0,
                reloadableRechargePointAmount,
                canUseRechargeAccount,
                canUseChargePointAccount,
            },
            payMoneyStateAvailable: isUsedLikePayMoney,
            chargePointAmount,
            payLaterUsed,
            availableMethod: isRechargePointAvailable,
        })
    },
)

/**
 * 네이버페이 머니카드 결제정책 (금감원)
 * 1. 적립포인트 + 네이버페이 머니카드 > 200만원
 * 2. 충전포인트(머니) + 네이버페이 머니카드 > 200만원
 * 1번 조건 or 2번 조건 하나라도 만족할 경우 결제 불가
 * @see https://wiki.fin.navercorp.com/spaces/FIN/pages/256409594/250508+%EB%A8%B8%EB%8B%88+or+%ED%8F%AC%EC%9D%B8%ED%8A%B8+%ED%8E%98%EC%9D%B4%EB%A8%B8%EB%8B%88%EC%B9%B4%EB%93%9C+%ED%95%A9%EC%82%B0+200%EB%A7%8C%EC%9B%90+%EC%B4%88%EA%B3%BC+%EC%8B%9C+%EC%A3%BC%EB%AC%B8%EC%A0%9C%ED%95%9C+%EC%B2%98%EB%A6%AC
 */
export const payMoneyCardAvailableSelector = createSelector(
    paymentAmountInfoSelectors,
    ({usingPayMethodAmount, usingRewardPointAmount, totalUsingChargePoint}) => {
        if (
            usingRewardPointAmount + usingPayMethodAmount > MaximumUsingChargePointAmount ||
            totalUsingChargePoint + usingPayMethodAmount > MaximumUsingChargePointAmount
        ) {
            return false
        }

        return true
    },
)

/** 페이머니 사용 가능 여부 selector
 * 선택 불가가 아니라 사용가능 여부 사유를 리턴.
 * 메시지 표기가 필요하기 때문에 rechargePaymentInfoSelector의 status보다 분류가 더 상세하고,
 * 복합결제 관련 사유가 포함되어있지 않음.
 * 현재 선택된 충전계좌 정보를 포함하여 랜더 후 충전결제 가능여부를 체크하는 것이 아니라 load 된 정보로 판단.
 * rechargePaymentInfoSelector와 status 는 충전결제가 선택되어을떄 판단, 아래는 선택되지 않아도 판단되어야함.
 **/
export const payMoneyDisabledSelector = createSelector(
    paymentSelector,
    payMoneyInfoSelector,
    chargePointSelector,
    rechargeAccountInfoSelector,
    paymentAmountInfoSelectors,
    accountSelector,
    (
        {complex},
        {isChargeLimitRequestFailed, payMoneyUserResultType, payMoneyUserStatus, isPayMoneyUser, availablePayment},
        {
            actualAmount: chargePointAmount,
            maximumUsingAmount,
            recharging,
            status: chargePointStatus,
            reloadable,
            nonCashMoneyAmount,
        },
        {maintenance},
        {paymentAmount},
        {payServiceInfo},
    ): {disabledReason: Maybe<Constant<typeof PaymoneyDisableCase>>} => {
        // @TODO 복합결제 및 은행점검시 사용불가 사유 payment-methods uiRechargePaymentSelector 영역과 복합결제 불가 사유 케이스만 분리 통합 필요
        const rechargeImmediately = recharging.processingType === 'IMMEDIATE'
        const enoughChargePoint = paymentAmount <= chargePointAmount

        const complexPaymentAvailable = complex.availableService && chargePointAmount > 0

        // 충전포인트 체크하는 서비스이면서, 보유포인트만으로 결제 가능한 상태
        const chargePointPaymentAvailable =
            complex.availableService && isPayMoneyUser && availablePayment && enoughChargePoint
        // 복합결제 가능한 결제수단이 존재하면서, 페이머니 유저여서 은행점검중에도 머니 사용하여 복합결제 가능한 상태
        const complexPaymentAvailableUnderBankMaintenance =
            complexPaymentAvailable && isPayMoneyUser && availablePayment

        // 보유포인트만으로 결제가능한 페이머니 유저거나 복합결제 가능상태가 아니면 은행점검중엔 사용불가. 잔액 조회 실패 케이스도 필요잔액을 계산할 수 없으므로 사용불가.
        const availableUnderBankMaintenance = chargePointPaymentAvailable || complexPaymentAvailableUnderBankMaintenance

        // 복합결제 불가서비스인데 최대사용가능액 초과시 사용 불가
        const isOverMaxPointUsageLimit =
            !complex.availableService && maximumUsingAmount !== null ? paymentAmount > maximumUsingAmount : false
        // 충전금액 지정필요한 서비스에서 최소 충전가능액 충족 못 할 경우
        const needRecharge = rechargeImmediately && (!isPayMoneyUser || (isPayMoneyUser && chargePointAmount === 0))
        const isReloadableRechargePointLack = needRecharge && reloadable.amount < PayPointAmount.MINIMUM_RECHARGE_AMOUNT

        // 전체은행 점검
        const allBankUnderMaintenance = maintenance?.status === 'ALL_BANK_INSPECT'
        // 서비스측 은행결제 점검
        const bankOnCircuitBreak = maintenance?.status === 'COMMON_INSPECT'
        // 서비스측 서버 에러
        const serviceOnError = payServiceInfo?.unavailableReason === 'SERVICE_SERVER_ERROR'
        // 페이머니 점검
        const payMoneyOnCircuitBreak = payMoneyUserResultType === PAYMONEY_RESULT_TYPE.FAILED

        // 보유 머니 기반으로 필요한 충전금액이나 복합결제 필요 금액을 계산해야하는데 현재 보유금 조회 실패한 경우.
        const needMoneyBalanceAndLoadFailed = complex.availableService && chargePointStatus === APIStatus.REJECTED

        // 페이머니 회원이 잔액조회 실패한 케이스. 단, 복합결제 가능서비스 & 페이머니회원은 잔액만 사용할 수도 있기때문에 disabled되지 않는다.
        const complexPaymentAvailableWithoutCharge = complex.availableService && isPayMoneyUser && chargePointAmount > 0
        const needRechargePointAndFailedLoadChargeLimit =
            isChargeLimitRequestFailed && !complexPaymentAvailableWithoutCharge

        // 페이머니 비회원이면 페이머니 시스템에서 잔액 조회 실패했으므로 페이머니 점검중이다
        const payMoneyFailedToLoadBalance = needMoneyBalanceAndLoadFailed && !isPayMoneyUser
        // 페이머니 회원이면 머니조회실패 == 은행 통장 잔고 조회 실패이므로 은행이 점검중이다
        const bankFailedToLoadBalance = needMoneyBalanceAndLoadFailed && isPayMoneyUser
        // 비현금성 머니 보유 + 포인트 사용 불가 가맹점 (머니 only 가맹점)
        const nonCashEvidenceIncluded = nonCashMoneyAmount === chargePointAmount && nonCashMoneyAmount > 0

        return {
            disabledReason: ((): Maybe<Constant<typeof PaymoneyDisableCase>> => {
                // 복합결제 불가 서비스거나 보유포인트만으로 결제불가한데 은행 전체 점검일 경우
                if ((allBankUnderMaintenance || bankOnCircuitBreak) && !availableUnderBankMaintenance) {
                    return PaymoneyDisableCase.ALL_BANK_UNDER_MAINTENANCE
                }

                // 잔액조회 결과로보아 페이머니 연결계좌 점검중일 때
                if (bankFailedToLoadBalance) {
                    return PaymoneyDisableCase.PAYMONEY_ACCOUNT_UNDER_MAINTENANCE
                }

                // 복합결제 불가 서비스거나 보유포인트만으로 결제불가한데 서비스 에러상황
                if (serviceOnError && !availableUnderBankMaintenance) {
                    return PaymoneyDisableCase.PAYMONEY_UNDER_MAINTENANCE
                }

                // 페이머니 서비스 점검 (서비스 오류상태이므로 재조회 불가)
                if (payMoneyOnCircuitBreak) {
                    return PaymoneyDisableCase.PAYMONEY_UNDER_MAINTENANCE
                }

                // 재조회시도 가능한 페이머니 정보 조회 실패 또는 잔액, 충전한도 조회 실패
                if (
                    payMoneyUserResultType === PAYMONEY_RESULT_TYPE.FAILED_RETRY_AVAILABLE ||
                    needRechargePointAndFailedLoadChargeLimit ||
                    payMoneyFailedToLoadBalance
                ) {
                    return PaymoneyDisableCase.PAYMONEY_INFO_LOAD_FAILURE
                }

                // 비현금성 머니 보유 + 포인트 사용 불가 가맹점 (머니 only 가맹점)
                if (nonCashEvidenceIncluded) {
                    return PaymoneyDisableCase.NON_CASH_EVIDENCE_INCLUDED_MONEY
                }

                // 머니 통장 연결 오류 (2.0 유저 케이스)
                if (payMoneyUserStatus === PayMoneyUserStatus.TRANSFER_FAIL) {
                    return PaymoneyDisableCase.PAYMONEY_TRANSFER_FAIL
                }

                // 페이머니 사용금 최대사용가능 금액 초과시
                if (isOverMaxPointUsageLimit) {
                    return PaymoneyDisableCase.AMOUNT_OVER_MAXIMUM_LIMIT
                }

                // 페이머니 통장 거래중지 케이스 (4.1 유저)
                if (payMoneyUserStatus === PayMoneyUserStatus.STOP_TRADING) {
                    return PaymoneyDisableCase.PAYMONEY_ACCOUNT_SUSPENDED
                }

                // 월간 충전가능 금액 소진으로 최소 충전가능 금액보다 충전한도가 부족한 상태
                if (isReloadableRechargePointLack) {
                    return PaymoneyDisableCase.NOT_ENOUGH_CHARGE_LIMIT
                }

                return null
            })(),
        }
    },
)
